import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, InputAdornment, Skeleton, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import RefreshIcon from "@mui/icons-material/Refresh";

import {
  IPD_COLORS,
  IPD_SHAPE,
  ipdCardSx,
  ipdElevatedInputSx,
  ipdPageSx,
  ipdSemanticSx,
  IpdButton,
  IpdPageHeader,
} from "../../../ipd/utils";
import { EmergencyPlaceholder } from "../../components/EmergencyBadges";
import { formatDateTime } from "../../constants";
import { getDeathRegister } from "../../api/emergencyApi";

/**
 * The hospital's unified death register (ticket 05) — every death, inpatient and
 * emergency, in one list tagged by source. Read-only; the emergency rows come
 * from the ED death records, the inpatient rows from the discharge process.
 */

const MANNER_LABEL = {
  natural: "Natural",
  accident: "Accident",
  suicide: "Suicide",
  homicide: "Homicide",
  pending_investigation: "Pending investigation",
};

function SourceChip({ source }) {
  const emergency = source === "emergency";
  return (
    <Box
      component="span"
      sx={{
        ...ipdSemanticSx(emergency ? "info" : "success"),
        px: 0.9,
        py: 0.2,
        borderRadius: `${IPD_SHAPE.radiusSm}px`,
        fontSize: 11,
        whiteSpace: "nowrap",
      }}
    >
      {emergency ? "Emergency" : "Inpatient"}
    </Box>
  );
}

function RegisterRow({ row }) {
  const cause = row.cause_pending
    ? "Cause pending"
    : row.cause_of_death || "Cause not recorded";
  const status = row.certificate_withheld
    ? { label: "Certificate withheld", variant: "warning" }
    : row.certificate_number
    ? { label: `Cert ${row.certificate_number}`, variant: "success" }
    : row.certified
    ? { label: "Certified", variant: "info" }
    : { label: "Uncertified", variant: "warning" };

  return (
    <Box
      sx={{
        ...ipdCardSx,
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.6fr) minmax(0, 1.4fr) auto" },
        alignItems: "center",
        gap: { xs: 1, md: 2 },
        p: 2,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: IPD_COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {row.patient_name || "Unknown"}
          </Typography>
          <SourceChip source={row.source} />
        </Box>
        <Typography sx={{ mt: 0.25, fontSize: 12, color: IPD_COLORS.textSecondary }}>
          {row.reference}
          {row.patient_id ? ` · ${row.patient_id}` : ""}
        </Typography>
      </Box>

      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, color: IPD_COLORS.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {cause}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: IPD_COLORS.textMuted }}>
          {row.death_datetime ? formatDateTime(row.death_datetime) : "Time not recorded"}
          {row.manner_of_death ? ` · ${MANNER_LABEL[row.manner_of_death] || row.manner_of_death}` : ""}
        </Typography>
      </Box>

      <Box
        component="span"
        sx={{
          ...ipdSemanticSx(status.variant),
          px: 0.9,
          py: 0.25,
          borderRadius: `${IPD_SHAPE.radiusSm}px`,
          fontSize: 11.5,
          whiteSpace: "nowrap",
          justifySelf: { md: "end" },
        }}
      >
        {status.label}
      </Box>
    </Box>
  );
}

export default function DeathRegister() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const reqIdRef = useRef(0);

  const load = useCallback(async () => {
    const reqId = ++reqIdRef.current;
    setLoading(true);
    try {
      const { rows: data } = await getDeathRegister();
      if (reqIdRef.current !== reqId) return;
      setRows(data);
      setError("");
    } catch (e) {
      if (reqIdRef.current !== reqId) return;
      setRows([]);
      setError(e?.message || "Could not load the death register");
    } finally {
      if (reqIdRef.current === reqId) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const q = search.trim().toLowerCase();
  const shown = q
    ? rows.filter((r) =>
        [r.patient_name, r.patient_id, r.reference, r.cause_of_death]
          .some((v) => (v || "").toLowerCase().includes(q))
      )
    : rows;

  return (
    <Box sx={{ ...ipdPageSx, p: { xs: 2, md: 3 } }}>
      <IpdPageHeader
        eyebrow="Emergency Department"
        title="Death Register"
        description="Every death in the hospital — inpatient and emergency — in one register."
        actions={
          <IpdButton variant="outlined" startIcon={<RefreshIcon />} onClick={load}>
            Refresh
          </IpdButton>
        }
      />

      <Box sx={{ display: "flex", gap: 2, mb: 2.5, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, identifier, reference or cause…"
          size="small"
          sx={{ ...ipdElevatedInputSx, flex: 1, minWidth: 260, maxWidth: 460 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: IPD_COLORS.textMuted }} />
              </InputAdornment>
            ),
          }}
          inputProps={{ "aria-label": "Search the death register" }}
        />
        {!loading && (
          <Typography sx={{ fontSize: 12.5, color: IPD_COLORS.textMuted }}>
            {shown.length} {shown.length === 1 ? "death" : "deaths"}
          </Typography>
        )}
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, borderRadius: `${IPD_SHAPE.radiusMd}px` }}
          action={
            <IpdButton variant="outlined" size="small" onClick={load}>
              Retry
            </IpdButton>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={72} sx={{ borderRadius: `${IPD_SHAPE.radiusMd}px` }} />
          ))}
        </Box>
      ) : shown.length === 0 ? (
        <EmergencyPlaceholder
          icon={MenuBookIcon}
          title={q ? "No matching deaths" : "No deaths recorded"}
          description={
            q
              ? "Try a different name, identifier or reference."
              : "Deaths appear here as visits close as expired and inpatient discharges are recorded as deceased."
          }
        />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {shown.map((r) => (
            <RegisterRow key={`${r.source}-${r.reference}`} row={r} />
          ))}
        </Box>
      )}
    </Box>
  );
}
